/**
 * JourneysHome — the Booking Journeys screen (route `journeys`), Operations
 * Admin rail. Ported from the source prototype's Journeys rail (Booking
 * journeys) — the list, the New-journey creator, the Branding tab and the full
 * editor/preview. The presentational pieces (OpsJourneysTab / JourneyCreator /
 * TenantBrandingPanel / JourneyEditor + panels + renderer) are the source's,
 * transcribed verbatim; THIS container owns data through the typed
 * @v0y4ge/api-client journeys ops (declared, REQ-063; preview-seeded until they
 * land — hence the amber DataSourceStrip). Tenant identity is RUNTIME context
 * (useTenant → whoami), never a baked constant (CC GM14 §4 item 1). NOTE: the
 * preview seed currently serves bank-world journey records for every tenant —
 * a flagged backend gap (per-tenant scoping lands with the /v1/journeys
 * contract-registration wave). WRITES await their ops (no fake interactivity).
 */
import { useEffect, useRef, useState } from "react";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { useWfResource, useBusy } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { toast } from "../../shell/toast";
import { T } from "@v0y4ge/jrni-ui";
import { ensureJourneysCss } from "./journeys-styles";
import { setActiveBrand, setJourneysBaseURL, type Journey } from "./journey-model";
import { OpsJourneysTab } from "./journey-list";
import JourneyEditor from "./JourneyEditor";
import { changeJourneyStatus } from "./journey-status";
import { useTenant } from "../../shell/access";
import { useT } from "@v0y4ge/i18n-runtime";
import {
  listJourneys, createJourney, deleteJourney, duplicateJourney,
  setJourneyStatus, getTenantBrand, type TenantBrand,
} from "./journeys-api";

export default function JourneysHome() {
  const t = useT();
  const nav = useNav();
  ensureJourneysCss();
  const tenant = useTenant();
  const slug = tenant?.slug ?? "";
  const { data, loading, error, reload } = useWfResource<{ journeys: Journey[] }>("journeys|" + slug, () => listJourneys(slug));
  const brandRes = useWfResource<TenantBrand>("journeys|brand|" + slug, () => getTenantBrand(slug));
  const { run } = useBusy();
  // URL is the source of truth for editor selection (deep-link/refresh/back-safe),
  // mirroring StaffEditorPage's nav.params.id pattern.
  const editingId = (nav.params?.id as string | undefined) ?? null;
  const setEditingId = (id: string | null) => nav.go("journeys", id ? { id } : {});
  // Local working copy so live editing + preview are instant; persisted to the op.
  const [working, setWorking] = useState<Journey[]>([]);
  useEffect(() => { if (data?.journeys) setWorking(data.journeys); }, [data]);
  useEffect(() => { if (brandRes.data) { setActiveBrand(brandRes.data as unknown as Record<string, string>); setJourneysBaseURL(brandRes.data.domain); } }, [brandRes.data]);

  const journeys = working;
  const warnedNoWrite = useRef(false);

  /**
   * ⛔ THIS EDITOR NO LONGER WRITES (drop 1, 2026-09-05).
   *
   * It used to PATCH the whole journey object on a 500ms debounce and swallow the
   * refusal (`.catch(() => {})`), so every edit looked saved and none was. The
   * replacement write path is the All Journeys screen's Appointments tab, which
   * patches whitelisted fields only. Until an editor edit has a contract-shaped
   * home, edits here stay local AND SAY SO — a control that appears to write and
   * does not is the thing the rule exists to prevent.
   */
  const onEditorChange = (nj: Journey) => {
    setWorking((js) => js.map((x) => (x.id === nj.id ? nj : x)));
    if (!warnedNoWrite.current) {
      warnedNoWrite.current = true;
      toast({
        tone: "warning",
        title: "Not saved",
        subtitle: "This editor's changes stay on screen only. Page settings save from All Journeys.",
      });
    }
  };
  const create = (j: Journey) => run("create", async () => {
    const created = await createJourney({ templateId: j.templateId, name: j.name, type: j.type, flowOrder: j.flowOrder });
    toast({ tone: "success", title: "Journey created", subtitle: `${created.name} · draft` });
    await reload();
    setEditingId(created.id);
  });
  const duplicate = (id: string) => run(`dup:${id}`, async () => {
    const copy = await duplicateJourney(id);
    toast({ tone: "success", title: "Journey duplicated", subtitle: `${copy.name} · draft` });
    reload();
  });
  const remove = (id: string) => run(`del:${id}`, async () => {
    const j = journeys.find((x) => x.id === id);
    await deleteJourney(id);
    toast({ tone: "success", title: "Journey deleted", subtitle: (j ? j.name : "Journey") + " removed" });
    reload();
  });
  const update = (nj: Journey) => run(`upd:${nj.id}`, async () => {
    // The only list-level write the contract accepts from this screen is the status op.
    if (nj.status === "draft") await setJourneyStatus(nj.id, "draft");
    else await setJourneyStatus(nj.id, "published");
    setWorking((js) => js.map((x) => (x.id === nj.id ? nj : x)));
  });
  /* The editor's Publish / Unpublish: the status op, awaited; the working copy takes the SERVER's status,
     updatedAt and publishedAt (CC 0529, journey-status.ts). Rejections propagate so the editor shows them.
     Only those fields are copied onto the working record, so its edits and its `_edited` mark stay: nothing
     here saves them, and the editor reads `_edited` to say so (CC 0530). The list's next reload from the
     server replaces the record and clears it. */
  const setEditorStatus = async (j: Journey, status: "draft" | "published"): Promise<Journey> => {
    const next = await changeJourneyStatus(j, status, setJourneyStatus);
    setWorking((js) => js.map((x) => (x.id === j.id ? { ...x, status: next.status, updatedAt: next.updatedAt, publishedAt: next.publishedAt, ...(next.publicUrl ? { publicUrl: next.publicUrl } : {}) } : x)));
    return next;
  };
  const editing = journeys.find((x) => x.id === editingId);
  const tLabel = tenant?.name ?? slug;

  return (
    <Screen rail="journeys">
      <div className="jrn-scope" style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
        {editing ? (
          <JourneyEditor journey={editing} onChange={onEditorChange} onBack={() => setEditingId(null)} onSetStatus={(s) => setEditorStatus(editing, s)} />
        ) : (
          <>
            <PageHeader title={t('journeys.home.title5')} breadcrumb="Estate configuration · All stores"
              subtitle={t('journeys.home.subtitle')} />
            <DataSourceStrip screen="journeys" ops={["listJourneys", "getTenantBrand", "createJourney", "updateAdminJourney", "deleteJourney", "duplicateJourney", "setJourneyStatus"]} />
            <div style={{ padding: "14px 24px 48px", display: "flex", flexDirection: "column", gap: 16 }}>
              {error ? <WfPending screen="journeys" opName="listJourneys" error={error} onRetry={reload} />
                : loading && !journeys.length ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("journeys.home.text")}</div>
                : <OpsJourneysTab journeys={journeys} tenantLabel={tLabel}
                    onOpen={setEditingId} onDuplicate={duplicate} onDelete={remove}
                    onUpdate={update} onCreate={create} />}
            </div>
          </>
        )}
      </div>
    </Screen>
  );
}
